// Phase 3 — OptiGuard adaptive settings + preview-only adjustment.
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Loader2, Save, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useShopPricingSettings, useUpsertPricingSettings,
  useShopPricingCategoryRules, useShopPricingMatrixRows, useUpsertMatrixRow,
  useShopPricingConfig,
} from '@/hooks/pricing/useShopPricingConfig';
import { useOptiGuardSuggestion } from '@/hooks/pricing/useOptiGuardSuggestion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Props { shopId: string; }

export function OptiGuardSettingsSection({ shopId }: Props) {
  const settingsQ = useShopPricingSettings(shopId);
  const rulesQ = useShopPricingCategoryRules(shopId);
  const matrixQ = useShopPricingMatrixRows(shopId);
  const upsertSettings = useUpsertPricingSettings();
  const upsertRow = useUpsertMatrixRow();
  const { config } = useShopPricingConfig(shopId);

  const [enabled, setEnabled] = useState(false);
  const [maxStep, setMaxStep] = useState(5.0);
  const [minSamples, setMinSamples] = useState(20);
  const [previewCategory, setPreviewCategory] = useState<string>('__default__');

  useEffect(() => {
    if (!settingsQ.data) return;
    setEnabled(settingsQ.data.optiguard_enabled);
    setMaxStep(Number(settingsQ.data.optiguard_max_step_pct));
    setMinSamples(Number(settingsQ.data.optiguard_min_sample_count));
  }, [settingsQ.data]);

  const previewCategoryName = previewCategory === '__default__' ? null : previewCategory;
  const targetGp = previewCategoryName
    ? rulesQ.data?.find((r) => r.category === previewCategoryName)?.optiguard_target_gp
    : null;

  const suggestion = useOptiGuardSuggestion({
    shopId,
    category: previewCategoryName,
    config,
    targetGpPct: targetGp,
    minSampleCount: minSamples,
    maxStepPct: maxStep,
  });

  const saveSettings = async () => {
    try {
      await upsertSettings.mutateAsync({
        shop_id: shopId,
        optiguard_enabled: enabled,
        optiguard_max_step_pct: maxStep,
        optiguard_min_sample_count: minSamples,
      });
      toast({ title: 'OptiGuard settings saved' });
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message, variant: 'destructive' });
    }
  };

  const applyAdjustment = async () => {
    if (!suggestion.adjustedMatrix) return;
    try {
      // Replace matrix rows for the selected category by upserting each.
      // We update existing rows in place by id where possible.
      const existing = matrixQ.data?.filter((r) =>
        previewCategoryName ? r.category === previewCategoryName : r.category == null
      ) ?? [];
      for (let i = 0; i < suggestion.adjustedMatrix.length; i++) {
        const adj = suggestion.adjustedMatrix[i];
        const ex = existing[i];
        if (!ex) continue;
        await upsertRow.mutateAsync({
          ...ex,
          multiplier: adj.multiplier,
        });
      }
      toast({ title: 'Adjustment applied to matrix' });
    } catch (e: any) {
      toast({ title: 'Apply failed', description: e?.message, variant: 'destructive' });
    }
  };

  const allCategories = rulesQ.data?.map((r) => r.category) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" /> OptiGuard Adaptive Pricing
        </CardTitle>
        <CardDescription>
          Suggests gradual matrix adjustments toward target gross profit %.
          Preview only — adjustments are never auto-applied.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center justify-between">
              Enable OptiGuard
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </Label>
          </div>
          <div className="space-y-2">
            <Label>Max step per update %</Label>
            <Input type="number" step="0.5" min="0.1"
              value={maxStep}
              onChange={(e) => setMaxStep(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Minimum sample count</Label>
            <Input type="number" step="1" min="1"
              value={minSamples}
              onChange={(e) => setMinSamples(Number(e.target.value))} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={saveSettings} disabled={upsertSettings.isPending}>
            {upsertSettings.isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            Save OptiGuard settings
          </Button>
        </div>

        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Preview adjustment for category:</Label>
            <Select value={previewCategory} onValueChange={setPreviewCategory}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">(default matrix)</SelectItem>
                {allCategories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md bg-muted/40 p-3 text-sm space-y-1">
            <div>
              <span className="text-muted-foreground">Sample count:</span>{' '}
              <span className="font-medium">{suggestion.sampleCount}</span>
              {' · '}
              <span className="text-muted-foreground">Historical GP%:</span>{' '}
              <span className="font-medium">
                {suggestion.historicalGpPct == null ? '—' : `${suggestion.historicalGpPct}%`}
              </span>
              {' · '}
              <span className="text-muted-foreground">Target GP%:</span>{' '}
              <span className="font-medium">{targetGp == null ? '— (set per-category)' : `${targetGp}%`}</span>
            </div>
            {suggestion.belowMinSamples && (
              <div className="flex items-center gap-1 text-xs text-amber-600">
                <AlertTriangle className="h-3 w-3" />
                Below minimum sample count — adjustment suppressed (outlier protection).
              </div>
            )}
            {suggestion.adjustedMatrix && (
              <div className="text-xs text-muted-foreground">
                Suggested new multipliers: {suggestion.adjustedMatrix.map((r) => r.multiplier).join(' · ')}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              disabled={!suggestion.adjustedMatrix || upsertRow.isPending}
              onClick={applyAdjustment}
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              Apply suggested adjustment
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
